/**
 * useDAGWorkflowData — Fetches DAG workflow definitions from GraphQL.
 *
 * Library-local gql queries (workflow-forms should not import from portal's
 * generated types). Falls back to sample data when backend is unavailable.
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import type { DAGWorkflowGQL } from './useWorkflowDAGLayout';

// ── GraphQL operations (inline — library package) ────────────────────

const DAG_WORKFLOW_DEFINITIONS = gql`
  query DagWorkflowDefinitions {
    dagWorkflowDefinitions {
      id
      name
      version
      description
      safetyLevel
      category
      nodes {
        id
        label
        nodeType
        criticality
        status
        phaseId
        phaseName
        phaseOrder
        timeoutMs
        requiredRole
        dependsOn
      }
      edges {
        id
        source
        target
        condition
        label
      }
      phases {
        phaseId
        name
        order
        engine
        description
      }
      createdAt
      createdBy
    }
  }
`;

const DAG_WORKFLOW_BY_ID = gql`
  query DagWorkflowById($workflowId: ID!) {
    dagWorkflow(workflowId: $workflowId) {
      id
      name
      version
      description
      safetyLevel
      category
      nodes {
        id
        label
        nodeType
        criticality
        status
        phaseId
        phaseName
        phaseOrder
        timeoutMs
        requiredRole
        dependsOn
      }
      edges {
        id
        source
        target
        condition
        label
      }
      phases {
        phaseId
        name
        order
        engine
        description
      }
      createdAt
      createdBy
    }
  }
`;

// ── Sample data (development fallback) ───────────────────────────────

const SAMPLE_WORKFLOWS: DAGWorkflowGQL[] = [
  {
    id: 'wf-0-0-001',
    name: 'Document PDF Signing',
    version: '1',
    description: 'Multi-step PDF signing workflow with approval gates',
    safetyLevel: 'CRITICAL',
    category: 'document_lifecycle',
    createdBy: 'config',
    nodes: [
      { id: 'add-annotations', nodeType: 'task', label: 'Add Annotations', criticality: 'lifeCritical', phaseId: 'annotate-and-review', phaseName: 'Annotate & Review', phaseOrder: 0, requiredRole: 'document_owner', dependsOn: [] },
      { id: 'review-annotations', nodeType: 'human', label: 'Review Annotations', criticality: 'lifeCritical', phaseId: 'annotate-and-review', phaseName: 'Annotate & Review', phaseOrder: 0, requiredRole: 'reviewer', dependsOn: ['add-annotations'] },
      { id: 'capture-signature', nodeType: 'human', label: 'Capture Signature', criticality: 'lifeCritical', phaseId: 'digital-signature', phaseName: 'Digital Signature', phaseOrder: 1, requiredRole: 'signer', dependsOn: [] },
      { id: 'apply-signature', nodeType: 'task', label: 'Apply Digital Signature', criticality: 'lifeCritical', phaseId: 'digital-signature', phaseName: 'Digital Signature', phaseOrder: 1, dependsOn: ['capture-signature'] },
      { id: 'upload-signed-pdf', nodeType: 'integration', label: 'Upload Signed PDF', criticality: 'complianceCritical', phaseId: 'digital-signature', phaseName: 'Digital Signature', phaseOrder: 1, dependsOn: ['apply-signature'] },
      { id: 'create-audit-event', nodeType: 'factSnapshot', label: 'Create Audit Event', criticality: 'complianceCritical', phaseId: 'audit-and-archive', phaseName: 'Audit & Archive', phaseOrder: 2, dependsOn: [] },
      { id: 'notify-stakeholders', nodeType: 'actorDispatch', label: 'Notify Stakeholders', criticality: 'operational', phaseId: 'audit-and-archive', phaseName: 'Audit & Archive', phaseOrder: 2, dependsOn: ['create-audit-event'] },
      { id: 'schedule-archive', nodeType: 'task', label: 'Schedule Archive', criticality: 'operational', phaseId: 'audit-and-archive', phaseName: 'Audit & Archive', phaseOrder: 2, dependsOn: ['create-audit-event'] },
    ],
    edges: [
      { id: 'add-annotations->review-annotations', source: 'add-annotations', target: 'review-annotations' },
      { id: 'review-annotations->capture-signature', source: 'review-annotations', target: 'capture-signature', label: 'phase: annotate-and-review → digital-signature' },
      { id: 'capture-signature->apply-signature', source: 'capture-signature', target: 'apply-signature' },
      { id: 'apply-signature->upload-signed-pdf', source: 'apply-signature', target: 'upload-signed-pdf' },
      { id: 'upload-signed-pdf->create-audit-event', source: 'upload-signed-pdf', target: 'create-audit-event', label: 'phase: digital-signature → audit-and-archive' },
      { id: 'create-audit-event->notify-stakeholders', source: 'create-audit-event', target: 'notify-stakeholders' },
      { id: 'create-audit-event->schedule-archive', source: 'create-audit-event', target: 'schedule-archive' },
    ],
    phases: [
      { phaseId: 'annotate-and-review', name: 'Annotate & Review', order: 0, engine: 'state_machine' },
      { phaseId: 'digital-signature', name: 'Digital Signature', order: 1, engine: 'state_machine' },
      { phaseId: 'audit-and-archive', name: 'Audit & Archive', order: 2, engine: 'dag_workflow' },
    ],
  },
];

// ── Hook ─────────────────────────────────────────────────────────────

export interface UseDAGWorkflowDataReturn {
  /** All available workflow definitions. */
  workflows: DAGWorkflowGQL[];
  /** Dropdown options for workflow selector. */
  workflowOptions: { value: string; label: string }[];
  /** Whether the GraphQL query is in flight. */
  loading: boolean;
  /** GraphQL error (null when using fallback). */
  error: Error | null;
  /** True when using sample data because backend is unavailable. */
  isFallback: boolean;
}

export function useDAGWorkflowData(): UseDAGWorkflowDataReturn {
  const { data, loading, error } = useQuery<{
    dagWorkflowDefinitions: DAGWorkflowGQL[];
  }>(DAG_WORKFLOW_DEFINITIONS, {
    // Don't throw — graceful fallback to sample data
    errorPolicy: 'all',
  });

  const workflows = useMemo(() => {
    const gqlWorkflows = data?.dagWorkflowDefinitions;
    if (gqlWorkflows && gqlWorkflows.length > 0) return gqlWorkflows;
    // Fallback to sample data when backend isn't running
    return SAMPLE_WORKFLOWS;
  }, [data]);

  const isFallback = !data?.dagWorkflowDefinitions?.length;

  const workflowOptions = useMemo(
    () => workflows.map((w) => ({ value: w.id, label: `${w.name} (${w.id})` })),
    [workflows],
  );

  return {
    workflows,
    workflowOptions,
    loading,
    error: error ?? null,
    isFallback,
  };
}

export { DAG_WORKFLOW_BY_ID };
